import { useEffect, useState, useMemo, useCallback } from 'react';
import { GrowthBook } from '@growthbook/growthbook-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AMSSelector,
  brandSelector,
  channelSelector,
  isNativeSelector,
  languageSelector,
  proxyPrefixSelector,
  userBalanceSelector,
  clientIdSelector,
  userCurrencyNameSelector,
  userNumberSelector,
} from '../redux/reducers/app/params';
import { addGrowthbookExperiment } from '../redux/actions/app/growthbookExperiments';
import { FEATURE_CONFIG_URL } from '../constants/api';
import { INITIAL_FEATURE_FLAGS } from '../constants/featureToggling';
import { analyticsLog } from '../utils/ga';
import { setGrowthBookLoaded } from '../redux/actions/app/experiment';
import type { GrowthbookExperiment } from '../redux/reducers/app/growthbookExperiments';

const initialValue = null;
let growthbook: GrowthBook = initialValue;

export const getGrowthBook = () => growthbook;

export const initGrowthBook = (
  isNative: boolean,
  addExperiment: (v: GrowthbookExperiment) => void
) => {
  growthbook = new GrowthBook({
    trackingCallback: (experiment, result) => {
      analyticsLog(
        {
          event: 'growthBook_exp_data',
          experiment_key: experiment.key,
          experiment_variant_id: result.variationId,
          ...(isNative ? { eventAction: 'growthBook_exp_data' } : {}),
        },
        isNative
      );

      addExperiment({ experiment, result });
    },
  });

  return growthbook;
};

export const useAttributes = () => {
  const accountNumber = useSelector(userNumberSelector);
  const currency = useSelector(userCurrencyNameSelector);
  const language = useSelector(languageSelector);
  const channel = useSelector(channelSelector);
  const brand = useSelector(brandSelector);
  const hostName = window.location.hostname;
  const balanceTotal = useSelector(userBalanceSelector);
  const amsCategories = useSelector(AMSSelector);

  // for growthbook -> client-id == fingerprint
  const fingerprint = useSelector(clientIdSelector);

  return useMemo(
    () => ({
      accountNumber,
      currency,
      language,
      channel,
      brand,
      hostName,
      balanceTotal,
      amsCategories,
      fingerprint,
    }),
    [
      accountNumber,
      currency,
      language,
      channel,
      brand,
      hostName,
      balanceTotal,
      amsCategories,
      fingerprint,
    ]
  );
};

export const useGrowthBook = () => {
  const [isLoading, setIsLoading] = useState(true);
  const attributes = useAttributes();
  const proxyPrefix = useSelector(proxyPrefixSelector);
  const isNative = useSelector(isNativeSelector);
  const dispatch = useDispatch();
  const addExperiment = useCallback(
    (experimentData) => dispatch(addGrowthbookExperiment(experimentData)),
    [dispatch]
  );
  const growthBook = useMemo(
    () => initGrowthBook(isNative, addExperiment),
    [isNative, addExperiment]
  );

  useEffect(() => {
    // Load feature definitions from API
    fetch(`${proxyPrefix}${FEATURE_CONFIG_URL}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error('Error during feature flags request');
        }

        return res.json();
      })
      .then((json) => {
        growthBook.setFeatures({ ...INITIAL_FEATURE_FLAGS, ...json.features });
      })
      .catch((error) => {
        console.error('Error during feature flags request', error);
        growthBook.setFeatures(INITIAL_FEATURE_FLAGS);
      })
      .finally(() => {
        setIsLoading(false);
        dispatch(setGrowthBookLoaded());
      });
  }, [proxyPrefix, growthBook, dispatch]);

  useEffect(() => {
    growthBook.setAttributes(attributes);
  }, [growthBook, attributes]);

  return { data: growthBook, isLoading };
};
